import { useEffect, useMemo, useState } from 'react';
import { formatNumber } from 'utils/number';
import { SidePanel } from '@carbon/ibm-products';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import 'components/inline-edit/ibm-products-config';
import type { AvailableAfe } from 'types/api-responses';
import type { VolumeSourcePickerData } from 'components/volume-source-picker';
import type { TractNRI } from '../types';
import { EditFieldsView } from './edit-fields-view';
import { ReviewAllSummary, ReviewChangesView } from './review-changes-view';
import {
  BulkEditAfeOptions,
  BulkEditFormInstance,
  BulkEditFormState,
  BulkEditView,
  emptyBulkEditForm,
  hasAnyValue,
  toBulkEditPatch,
} from './types';
import { AFE_PICKER_INPUT_ID } from './utils';

/**
 * The flat, enumerated save payload — UNCHANGED, still consumed by the legacy
 * `nri-table` + eval-result callsites (and eval3's `applyBulkEditToOverrides`). The
 * scoped `enumerated` variant below is exactly this tagged with `scope`.
 */
export interface BulkEditSavePayload {
  slotIds: string[];
  patch: Partial<BulkEditFormState>;
  /**
   * The full AvailableAfe record corresponding to `patch.afeSourceId`, if the user
   * picked one in this draft — lets the save handler write the resolved href / well
   * name / costs onto each slot's AFE configuration.
   */
  resolvedAfe?: AvailableAfe;
}

/**
 * The panel's bulk scope — a colocated discriminated union so each mode carries ONLY
 * the props it uses (no required-but-ignored props). `enumerated` = the classic
 * checkbox selection (materialized rows + AFE options + per-row review). `all` = the
 * header select-all bulk (count only for the subtitle; the caller owns the exclusion
 * set). The genuinely-shared props (`open` / `onClose` / `onSave`) stay top-level.
 */
export type BulkEditScope =
  | {
      kind: 'enumerated';
      selectedRows: TractNRI[];
      allRows?: TractNRI[];
      afeOptions: BulkEditAfeOptions;
      resolveOriginal?: (
        row: TractNRI,
        key: keyof BulkEditFormState
      ) => string | undefined;
    }
  | {
      kind: 'all';
      total: number;
      excludedCount: number;
      /**
       * The select-all AFE picker inputs — a catalog-backed, server-searched, infinite-scroll
       * list. The caller (slot-view) owns the selection + search; the panel forwards these to
       * the shared `SRPComboBox` and reads `selectedAfe` for the Continue gate + review.
       */
      afe: {
        items: AvailableAfe[];
        status: BulkEditAfeOptions['status'];
        errorMessage?: string;
        selectedAfe: AvailableAfe | null;
        onSelect: (afe: AvailableAfe | null) => void;
        onSearchChange: (text: string) => void;
        onLoadMore: () => void;
        hasNextPage: boolean;
        isFetchingNextPage: boolean;
      };
    };

/**
 * What the panel emits on Save, discriminated by scope. The `enumerated` variant is the
 * flat `BulkEditSavePayload` tagged with `scope` (so existing consumers keep the flat
 * shape once the tag is stripped); the `all` variant carries just the patch — the caller
 * supplies the exclusion set from its own selection state.
 */
export type BulkEditScopedSavePayload =
  | ({ scope: 'enumerated' } & BulkEditSavePayload)
  | { scope: 'all'; patch: Partial<BulkEditFormState> };

interface BulkEditSlotsPanelProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the discriminated payload on Save (blank fields already stripped).
   * `enumerated` → slot ids + resolved AFE; `all` → just the patch (the caller applies
   * it to all-minus-its-own-exclusions).
   */
  onSave?: (payload: BulkEditScopedSavePayload) => void;
  /** The bulk scope + its mode-specific inputs. */
  scope: BulkEditScope;
  /**
   * Volume-source picker inputs (INS-11129). **Omit to hide the section entirely**, so
   * consumers that do not offer volume overrides are unaffected.
   */
  volumeSource?: VolumeSourcePickerData;
}

/** Placeholder AFE options for select-all mode (the AFE section is hidden there). */
const NO_AFE_OPTIONS: BulkEditAfeOptions = { status: 'empty', items: [] };

/** Stable empty rows for select-all mode, so the memo deps don't churn each render. */
const EMPTY_ROWS: TractNRI[] = [];

/**
 * Maps `TractNRI` rows to the minimal slot summaries the edit view needs.
 *
 * @param {TractNRI[]} rows - The rows to summarize.
 * @return {object[]} The `{ slotId, afeWellName, afeSourceId }` summaries.
 */
const toSlotSummary = (rows: TractNRI[]) =>
  rows
    .filter((row): row is TractNRI & { slotId: string } => Boolean(row.slotId))
    .map((row) => ({
      slotId: row.slotId,
      afeWellName: row.afeWellName,
      afeSourceId: row.afeSourceId,
    }));

/**
 * Presentational bulk-edit drawer for slots — a two-step (edit → review) side panel
 * that composes one patch and applies it across a scope. In `enumerated` mode it edits
 * the ticked rows (full AFE + dates, per-row review); in `all` mode it edits every slot
 * minus the caller's exclusions (dates only — AFE hidden — with a count-based summary).
 * Data-source-agnostic: the AFE candidate list rides `scope.afeOptions` (Jotai via
 * `BulkEditSlotsPanelConnected` for legacy, React Query for eval3). The draft form is
 * local + transient; on Save the normalized patch (scoped) is handed to `onSave`.
 *
 * @param {BulkEditSlotsPanelProps} props - Open state, the save callback, and the scope.
 * @return {JSX.Element} The bulk-edit side panel.
 */
export const BulkEditSlotsPanel = ({
  open,
  onClose,
  onSave,
  scope,
  volumeSource,
}: BulkEditSlotsPanelProps) => {
  const [view, setView] = useState<BulkEditView>('edit');

  // Form state persists locally as the user types and survives X / overlay / ESC
  // dismissals; it's cleared only on Cancel or a successful Save.
  const form = useForm({
    defaultValues: emptyBulkEditForm,
  }) as unknown as BulkEditFormInstance;

  // When the panel reopens fresh, snap back to the edit view. Form values are kept.
  useEffect(() => {
    if (open) {
      setView('edit');
    }
  }, [open]);

  // Enumerated-only inputs (empty / placeholder in select-all mode).
  const enumeratedRows =
    scope.kind === 'enumerated' ? scope.selectedRows : EMPTY_ROWS;
  const selectedSlots = useMemo(
    () => toSlotSummary(enumeratedRows),
    [enumeratedRows]
  );
  const allSlots = useMemo(() => {
    const source =
      scope.kind === 'enumerated' && scope.allRows
        ? scope.allRows
        : enumeratedRows;
    return toSlotSummary(source);
  }, [scope, enumeratedRows]);
  const selectedSlotIds = useMemo(
    () => selectedSlots.map((s) => s.slotId),
    [selectedSlots]
  );
  const afeOptions =
    scope.kind === 'enumerated' ? scope.afeOptions : NO_AFE_OPTIONS;

  const subtitle =
    scope.kind === 'all'
      ? `All ${formatNumber(scope.total, 0)} slot${
          scope.total === 1 ? '' : 's'
        }${
          scope.excludedCount > 0
            ? ` (${formatNumber(scope.excludedCount, 0)} excluded)`
            : ''
        }`
      : `${scope.selectedRows.length} slot${
          scope.selectedRows.length === 1 ? '' : 's'
        } selected`;

  const formValues = useStore(form.store, (s) => s.values);
  // In `all` mode the AFE is slot-view state (not a form value), so an AFE-only select-all
  // must still enable Continue.
  const continueDisabled =
    scope.kind === 'all'
      ? !(hasAnyValue(formValues) || scope.afe.selectedAfe)
      : !hasAnyValue(formValues);
  // The normalized patch (blank fields stripped) — derived once so the save payload and
  // the `all`-mode review summary share one stable object (keeps its own memo warm).
  const patch = useMemo(() => toBulkEditPatch(formValues), [formValues]);

  const handleCancel = () => {
    form.reset();
    setView('edit');
    onClose();
  };

  const handleSave = () => {
    if (scope.kind === 'all') {
      onSave?.({ scope: 'all', patch });
    } else {
      const resolvedAfe = patch.afeSourceId
        ? afeOptions.items.find((a) => a.id === patch.afeSourceId)
        : undefined;
      onSave?.({
        scope: 'enumerated',
        slotIds: selectedSlotIds,
        patch,
        resolvedAfe,
      });
    }
    form.reset();
    setView('edit');
    onClose();
  };

  const actions =
    view === 'edit'
      ? [
          {
            kind: 'secondary' as const,
            label: 'Cancel',
            onClick: handleCancel,
          },
          {
            kind: 'primary' as const,
            label: 'Continue',
            disabled: continueDisabled,
            onClick: () => setView('confirm'),
          },
        ]
      : [
          {
            kind: 'secondary' as const,
            label: 'Cancel',
            onClick: handleCancel,
          },
          {
            kind: 'primary' as const,
            label: 'Save',
            // Continue gate already enforces at least one value; belt-and-suspenders.
            disabled: continueDisabled,
            onClick: handleSave,
          },
        ];

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Bulk Edit Slots"
      subtitle={subtitle}
      placement="right"
      size="md"
      animateTitle={false}
      actions={actions}
      // In select-all mode the AFE picker paginates: as the user scrolls the catalog, the
      // SidePanel re-renders (its content resizes) and its focus effect would otherwise grab
      // the close button, blurring the combobox input and closing the open menu. Point that
      // focus effect at the AFE input instead so the refocus is a no-op and the menu stays
      // open. Scoped to `all` — enumerated/legacy keep the default first-element focus.
      selectorPrimaryFocus={
        scope.kind === 'all' ? `#${AFE_PICKER_INPUT_ID}` : undefined
      }
    >
      {view === 'edit' && (
        <EditFieldsView
          form={form}
          volumeSource={volumeSource}
          selectedSlots={selectedSlots}
          allSlots={allSlots}
          afe={
            scope.kind === 'all'
              ? { mode: 'all', ...scope.afe }
              : { mode: 'enumerated', options: afeOptions }
          }
          allSummary={
            scope.kind === 'all'
              ? { total: scope.total, excludedCount: scope.excludedCount }
              : undefined
          }
        />
      )}
      {view === 'confirm' &&
        (scope.kind === 'all' ? (
          <ReviewAllSummary
            patch={patch}
            total={scope.total}
            excludedCount={scope.excludedCount}
            selectedAfe={scope.afe.selectedAfe}
            onBack={() => setView('edit')}
            volumeForecasts={volumeSource?.forecasts ?? null}
          />
        ) : (
          <ReviewChangesView
            selectedRows={scope.selectedRows}
            form={formValues}
            afeOptions={scope.afeOptions}
            onBack={() => setView('edit')}
            resolveOriginal={scope.resolveOriginal}
            volumeForecasts={volumeSource?.forecasts ?? null}
          />
        ))}
    </SidePanel>
  );
};
